import { Link } from 'react-router-dom';
import { useAppStore } from '../hooks/useAppStore';
import { useToast } from '../hooks/useToast';
import { copyToClipboard } from '../api/mock';
import Toast from '../components/Toast';
import { Copy, ChevronRight, CheckCircle, Circle, UserPlus } from 'lucide-react';
import * as Icons from 'lucide-react';

export default function Dashboard() {
  const { user, topics, partners, completedCount } = useAppStore();
  const toast = useToast();

  const handleCopyId = async () => { await copyToClipboard(user.profileId); toast.show('Profile ID copied!'); };

  return (
    <div className="px-4 py-5 pb-8 space-y-6 animate-fade-in">
      {/* Profile Card */}
      <div className="glass p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-lg font-bold text-gray-100">{user.displayName}</p>
            <p className="text-xs text-gray-500">Your Profile</p>
          </div>
          <div className="w-11 h-11 rounded-full bg-burner-500/15 border border-burner-500/25 flex items-center justify-center text-burner-400 font-bold text-lg">{user.displayName[0]}</div>
        </div>
        <button onClick={handleCopyId} className="w-full flex items-center justify-between bg-zinc-800/60 rounded-xl px-4 py-3 border border-white/[0.04] hover:border-burner-500/20 transition-all">
          <div><p className="section-label mb-0.5">Your Profile ID</p><p className="font-mono text-sm text-burner-400 font-semibold tracking-wider">{user.profileId}</p></div>
          <Copy size={16} className="text-gray-500" />
        </button>
      </div>

      {/* Topics Progress */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-100">Your Topics</h2>
          <span className="text-xs text-gray-500">{completedCount} of {topics.length} complete</span>
        </div>
        <div className="h-1 bg-zinc-800 rounded-full overflow-hidden mb-4">
          <div className="h-full bg-burner-500 rounded-full transition-all duration-700" style={{width:`${(completedCount/topics.length)*100}%`}} />
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {topics.map(t => {
            const Icon = Icons[t.icon] || Icons.Circle;
            return (
              <Link to={`/app/topic/${t.id}`} key={t.id} className="glass-sm p-4 glass-hover group">
                <div className="flex items-center justify-between mb-2">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{background:`${t.color}15`, border:`1px solid ${t.color}25`}}>
                    <Icon size={18} style={{color:t.color}} />
                  </div>
                  {t.completed ? <CheckCircle size={16} className="text-emerald-500" /> : <Circle size={16} className="text-zinc-600" />}
                </div>
                <p className="text-sm font-medium text-gray-100 group-hover:text-burner-400 transition-colors">{t.name}</p>
                <p className="text-[11px] text-gray-500 mt-0.5">{t.completed ? 'Complete' : 'Fill in'}</p>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Partners */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-gray-100">Partners</h2>
          <Link to="/app/invite" className="text-xs text-burner-400 hover:text-burner-300 transition-colors">+ Invite</Link>
        </div>
        <div className="space-y-2.5">
          {partners.map(p => (
            <Link to={`/app/partner/${p.id}`} key={p.id} className="glass-sm p-4 flex items-center gap-3.5 glass-hover">
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{background: p.status==='active'?'rgba(16,185,129,0.12)':'rgba(249,115,22,0.1)', border:`1px solid ${p.status==='active'?'rgba(16,185,129,0.25)':'rgba(249,115,22,0.2)'}`, color: p.status==='active'?'#10b981':'#fb923c'}}>
                {p.initial}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-100">{p.displayName}</p>
                <p className="text-[11px] text-gray-500">{p.status==='active' ? `${p.mutualTopics.length} shared topics` : 'Pending — no topics shared yet'}</p>
              </div>
              <ChevronRight size={16} className="text-gray-600" />
            </Link>
          ))}
          <Link to="/app/invite" className="glass-sm p-4 flex items-center gap-3.5 glass-hover border-dashed">
            <div className="w-10 h-10 rounded-full bg-zinc-800/60 border border-dashed border-zinc-600 flex items-center justify-center"><UserPlus size={18} className="text-gray-500" /></div>
            <p className="text-sm text-gray-400">Invite a partner</p>
          </Link>
        </div>
      </div>

      <Toast message={toast.message} onDismiss={toast.dismiss} />
    </div>
  );
}
